import { Link } from 'react-router-dom'

import { hasScopesPermission } from '@tribeplatform/gql-client/lib'
import { dayjs } from '@tribeplatform/react-components/common/lib'
import { I18nShape, T, useI18n } from '@tribeplatform/react-components/i18n'
import {
  useAuthToken,
  useModerationCount,
} from '@tribeplatform/react-sdk/hooks'
import { Button } from '@tribeplatform/react-ui-kit/Button'
import { Card } from '@tribeplatform/react-ui-kit/Card'
import { SvgIcon } from '@tribeplatform/react-ui-kit/Icon'
import { Container } from '@tribeplatform/react-ui-kit/Layout'

const getWelcomeMessageText = ({ $t }: I18nShape) => {
  const hour = parseInt(dayjs().format('HH'), 10)
  let message
  if (hour < 12) {
    message = $t({
      defaultMessage: 'Good morning',
      id: 'Greetings.Morning',
    })
  } else if (hour < 18) {
    message = $t({
      defaultMessage: 'Good afternoon',
      id: 'Greetings.Afternoon',
    })
  } else {
    message = $t({
      defaultMessage: 'Good evening',
      id: 'Greetings.Evening',
    })
  }
  return message
}

export const ModerationSummary = ({ spaceId = undefined }) => {
  const intl = useI18n()
  const { $t } = intl
  const {
    data: { network },
  } = useAuthToken()

  const [canUpdateNetwork] = hasScopesPermission(network, ['updateNetwork'])

  const {
    pendingPostsCount: totalPendingPosts,
    pendingMembersCount: totalSpaceJoinRequests,
    reportedMembersCount,
    reportedPostsCount,
    scheduledPostsCount,
    draftPostsCount,
    total,
  } = useModerationCount(spaceId)

  return (
    <Card>
      <Card.Header
        title={$t({ id: 'Generics.Summary', defaultMessage: 'Summary' })}
      />
      <Card.Content>
        <Container size="full" direction="vertical">
          <p>{getWelcomeMessageText(intl)},</p>
          <Container size="full" direction="vertical" spacing="none">
            {draftPostsCount > 0 && (
              <p>
                <T
                  defaultMessage="{draft_posts, plural, =1 {# post is} other {# posts are}} in draft."
                  id="Moderation.Summary.DraftPosts"
                  values={{
                    draft_posts: draftPostsCount,
                  }}
                />
              </p>
            )}
            {scheduledPostsCount > 0 && (
              <p>
                <T
                  defaultMessage="{scheduled_posts, plural, =1 {# post is scheduled} other {# posts are scheduled}}."
                  id="Moderation.Summary.ScheduledPosts"
                  values={{
                    scheduled_posts: scheduledPostsCount,
                  }}
                />
              </p>
            )}
            {totalPendingPosts > 0 && (
              <p>
                <T
                  defaultMessage="{pending_posts, plural, =1 {# post requires} other {# posts require}} moderation."
                  id="Moderation.Summary.PendingPosts"
                  values={{
                    pending_posts: totalPendingPosts,
                  }}
                />
              </p>
            )}
            {reportedPostsCount > 0 && (
              <p>
                <T
                  defaultMessage="{pending_posts, plural, =1 {# post has been reported} other {# posts have been reported}}."
                  id="Moderation.Summary.ReportedPosts"
                  values={{
                    pending_posts: reportedPostsCount,
                  }}
                />
              </p>
            )}
            {totalSpaceJoinRequests > 0 && (
              <p>
                <T
                  defaultMessage="{member_requests, plural, =1 {# {MEMBER} is} other {# {MEMBERS} are}} awaiting approval."
                  id="Moderation.Summary.PendingMembers"
                  values={{
                    member_requests: totalSpaceJoinRequests,
                  }}
                />
              </p>
            )}
            {reportedMembersCount > 0 && (
              <p>
                <T
                  defaultMessage="{member_requests, plural, =1 {# {MEMBER} has been} other {# {MEMBERS} have been}} reported."
                  id="Moderation.Summary.ReportedMembers"
                  values={{
                    member_requests: reportedMembersCount,
                  }}
                />
              </p>
            )}
            {total === 0 && (
              <p>
                <T
                  defaultMessage="Nothing to moderate here."
                  id="Moderation.Summary.EmptyState"
                />
              </p>
            )}
          </Container>
          {canUpdateNetwork && (
            <Button
              variant="secondaryNeutral"
              fullWidth
              as={Link}
              to="/manage/settings/moderation"
              leadingIcon={<SvgIcon name="settings" />}
            >
              <T
                defaultMessage="Moderation settings"
                id="Moderation.Summary.GoToSettings"
              />
            </Button>
          )}
        </Container>
      </Card.Content>
    </Card>
  )
}
